import { useEffect, useRef } from "react";
import {
  formatSsn,
  parseEmail,
  parsePhoneNumber,
  parseSsn,
} from "~/common/converters";
import { formatUsPhoneNumber } from "~/common/phoneNumbers";
import TextArea, { TextAreaBlockProps } from "./TextArea";

type StringType = "phoneNumber" | "SSN" | "email";
type NumberType = "dollarAmount" | "weight" | "number";
type FormattedTextAreaType = StringType | NumberType;

type StringTypeProps = {
  type: StringType;
  value: string;
  onChange: (value: string) => void;
};

type NumberTypeProps = {
  type: NumberType;
  value: number | null;
  onChange: (value: number | null) => void;
  isInteger?: boolean;
};

type TypeSpecificProps = StringTypeProps | NumberTypeProps;

export type FormattedTextAreaBlockProps = Omit<
  TextAreaBlockProps,
  "value" | "onChange"
> &
  TypeSpecificProps & {
    "data-testid"?: string;
  };

// Type guards to narrow types
function isNumberType(type: FormattedTextAreaType): type is NumberType {
  return type === "dollarAmount" || type === "weight" || type === "number";
}

function isNumberTypeProps(
  props: FormattedTextAreaBlockProps,
): props is Omit<TextAreaBlockProps, "value" | "onChange"> & NumberTypeProps {
  return isNumberType(props.type);
}

// Type-safe formatters with overloads
function formatValue(
  type: StringType,
  value: string,
  disabled?: boolean,
): string;
function formatValue(
  type: NumberType,
  value: number | null,
  isInteger?: boolean,
  disabled?: boolean,
): string;
function formatValue(
  type: FormattedTextAreaType,
  value: string | number | null,
  isIntegerOrDisabled?: boolean,
  disabled?: boolean,
): string {
  const isInteger =
    typeof isIntegerOrDisabled === "boolean" ? isIntegerOrDisabled : false;
  const isDisabled = typeof disabled === "boolean" ? disabled : false;

  switch (type) {
    case "phoneNumber":
      return formatUsPhoneNumber(value as string);
    case "SSN":
      return formatSsn(value as string);
    case "email":
      return value as string; // No special display formatting needed for email
    case "dollarAmount": {
      if (value === null) return "";
      const dollarValue =
        typeof value === "number" && !Number.isInteger(value)
          ? Number(value.toFixed(2))
          : value;
      return `$${dollarValue}`;
    }
    case "weight": {
      if (value === null) return "";
      const weightValue =
        typeof value === "number" && !Number.isInteger(value)
          ? Number(value.toFixed(2))
          : value;
      // Only add "lbs" suffix when disabled
      return isDisabled ? `${weightValue} lbs` : `${weightValue}`;
    }
    case "number":
      if (value === null) return "";
      if (isInteger && value === 0) return "";

      if (!isInteger && typeof value === "number" && !Number.isInteger(value)) {
        return value.toString();
      }

      return value.toString();
  }
}

const cleanNumericInput = (
  value: string,
  isInteger = false,
  type: NumberType,
): number | null => {
  if (isInteger) {
    const cleaned = value.replace(/[^\d]/g, "");

    if (cleaned === "") {
      return null;
    }

    return parseInt(cleaned, 10);
  }

  // For non-integer fields, handle decimals
  let cleaned = value.replace(/[^\d.]/g, "");

  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }

  if (type === "dollarAmount" && parts.length === 2 && parts[1].length > 2) {
    // Only apply 2 decimal precision for dollarAmount
    cleaned = parts[0] + "." + parts[1].substring(0, 2);
  }

  if (cleaned === "" || cleaned === ".") {
    return null;
  }

  const num = parseFloat(cleaned);
  const hasDecimal = cleaned.includes(".");

  if (hasDecimal && type === "dollarAmount") {
    // Only apply 2 decimal precision for dollarAmount
    return Number(num.toFixed(2));
  }

  return num;
};

const parseStringValue = (type: StringType, value: string): string => {
  switch (type) {
    case "phoneNumber":
      return parsePhoneNumber(value);
    case "SSN":
      return parseSsn(value);
    case "email":
      return parseEmail(value);
  }
};

function cleanNumberInput(
  type: NumberType,
  value: number | string,
  isInteger?: boolean,
): number | null {
  if (value === null || value === "") {
    return null;
  }
  const valueStr = typeof value === "number" ? value.toString() : String(value);
  return cleanNumericInput(valueStr, isInteger, type);
}

export function FormattedTextAreaBlock<T extends StringType>(
  props: Omit<TextAreaBlockProps, "value" | "onChange"> & {
    type: T;
    value: string;
    onChange: (value: string) => void;
    "data-testid"?: string;
  },
): JSX.Element;

export function FormattedTextAreaBlock<T extends NumberType>(
  props: Omit<TextAreaBlockProps, "value" | "onChange"> & {
    type: T;
    value: number | null;
    onChange: (value: number | null) => void;
    isInteger?: boolean;
    "data-testid"?: string;
  },
): JSX.Element;

export function FormattedTextAreaBlock(
  props: FormattedTextAreaBlockProps,
): JSX.Element {
  if (isNumberTypeProps(props)) {
    return <NumberFormattedTextArea {...props} />;
  }

  return <StringFormattedTextArea {...props} />;
}

function StringFormattedTextArea(
  props: Omit<TextAreaBlockProps, "value" | "onChange"> &
    StringTypeProps & {
      "data-testid"?: string;
    },
): JSX.Element {
  const { type, disabled, value, onChange, isUnavailable, ...additionalProps } =
    props;

  /**
   * This effect ensures input values are automatically cleaned and formatted.
   * When a value is provided, it's processed according to its type
   * and automatically corrected if needed
   */
  useEffect(() => {
    if (value !== null && value !== undefined) {
      const cleanedValue = parseStringValue(type, String(value));
      if (cleanedValue !== value) {
        onChange(cleanedValue);
      }
    }
  }, [type, value, onChange]);

  const handleChange = (newValue: string) => {
    const cleanedValue = parseStringValue(type, newValue);
    if (cleanedValue !== value) {
      onChange(cleanedValue);
    }
  };

  return (
    <TextArea
      {...additionalProps}
      disabled={disabled}
      isUnavailable={isUnavailable}
      value={formatValue(type, value, disabled)}
      onChange={handleChange}
    />
  );
}

function NumberFormattedTextArea(
  props: Omit<TextAreaBlockProps, "value" | "onChange"> &
    NumberTypeProps & {
      "data-testid"?: string;
    },
): JSX.Element {
  const {
    type,
    disabled,
    value,
    onChange,
    isInteger,
    isUnavailable,
    ...additionalProps
  } = props;

  /**
   * Ref to store the last input that ended with a decimal point.
   * This allows us to preserve the decimal point during input, which is otherwise
   * lost when converting to a number with parseFloat.
   *
   * Example: When user types "313.", parseFloat("313.") returns 313 without the decimal.
   * We store the raw input to display it correctly while still using the numeric value
   * for the component's state.
   *
   * Using a ref instead of a global variable ensures each component instance
   * has its own state and prevents interference between multiple instances.
   */
  const lastDotInputRef = useRef<string>("");

  /**
   * This effect ensures input values are automatically cleaned and formatted.
   * When a value is provided, it's processed according to its type
   * and automatically corrected if needed (e.g., ensuring decimal precision for currency values).
   *
   * For dollar amounts, we maintain 2 decimal places.
   * For other number types, we preserve the original precision.
   */
  useEffect(() => {
    if (value !== null && value !== undefined) {
      const cleanedValue = cleanNumberInput(type, value, isInteger);
      if (cleanedValue !== value) {
        onChange(cleanedValue);
      }
    }
  }, [type, value, onChange, isInteger]);

  const handleChange = (newValue: string) => {
    if (newValue.trim() === "") {
      onChange(null);
      lastDotInputRef.current = "";
      return;
    }

    let valueToProcess = newValue;

    switch (type) {
      case "dollarAmount":
        if (newValue.startsWith("$")) {
          valueToProcess = newValue.substring(1);
        }
        break;
      case "weight":
        if (newValue.endsWith(" lbs")) {
          valueToProcess = newValue.substring(0, newValue.length - 4);
        }
        break;
    }

    if (isInteger) {
      valueToProcess = valueToProcess.replace(/\./g, "");
      const cleanedValue = cleanNumberInput(type, valueToProcess, isInteger);
      onChange(cleanedValue);
      return;
    }

    // Handle decimal points for non-integer fields
    const decimalCount = (valueToProcess.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const parts = valueToProcess.split(".");
      valueToProcess = parts[0] + "." + parts.slice(1).join("");
    }

    if (valueToProcess.endsWith(".")) {
      switch (type) {
        case "dollarAmount":
          lastDotInputRef.current = valueToProcess.startsWith("$")
            ? valueToProcess
            : `$${valueToProcess}`;
          break;
        case "weight":
          lastDotInputRef.current = valueToProcess.endsWith(" lbs")
            ? valueToProcess
            : `${valueToProcess} lbs`;
          break;
        default:
          lastDotInputRef.current = valueToProcess;
      }

      const numPart = valueToProcess.substring(0, valueToProcess.length - 1);
      const numValue = numPart ? Number(parseInt(numPart, 10)) : 0;
      onChange(numValue);
      return;
    }

    lastDotInputRef.current = "";

    const cleanedValue = cleanNumberInput(type, valueToProcess, isInteger);
    onChange(cleanedValue);
  };

  const getDisplayValue = (): string => {
    if (lastDotInputRef.current && value !== null) {
      let valueToCheck = lastDotInputRef.current;

      switch (type) {
        case "dollarAmount":
          if (lastDotInputRef.current.startsWith("$")) {
            valueToCheck = lastDotInputRef.current.substring(1);
          }
          break;
        case "weight":
          if (lastDotInputRef.current.endsWith(" lbs")) {
            valueToCheck = lastDotInputRef.current.substring(
              0,
              lastDotInputRef.current.length - 4,
            );
          }
          break;
      }

      if (valueToCheck.includes(".")) {
        const parts = valueToCheck.split(".");
        valueToCheck = parts[0] + "." + parts.slice(1).join("");
      }

      if (valueToCheck.endsWith(".")) {
        const numPart = valueToCheck.substring(0, valueToCheck.length - 1);
        const numValue = numPart ? Number(parseInt(numPart, 10)) : 0;

        if (numValue === value) {
          return lastDotInputRef.current;
        }
      }
    }

    if (value === null) {
      return "";
    }

    return formatValue(type, value, isInteger, disabled);
  };

  // Remove non-TextAreaBlock props
  let propagatingProps = { ...additionalProps };
  if ("isInteger" in additionalProps) {
    const { isInteger: _isInteger, ...rest } = additionalProps;
    propagatingProps = rest;
  }

  return (
    <TextArea
      {...propagatingProps}
      disabled={disabled}
      isUnavailable={isUnavailable}
      value={getDisplayValue()}
      onChange={handleChange}
    />
  );
}
